const STORAGE_KEY = 'duos_attribution';

export interface Attribution {
  ref?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
}

const ATTRIBUTION_PARAMS = [
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
];

/**
 * Captures attribution parameters from current URL and stores in localStorage.
 * Only stores if at least one attribution param exists and not already stored.
 * Removes attribution params from URL after capture to keep it clean.
 */
export function captureAttribution(): void {
  const params = new URLSearchParams(window.location.search);

  const ref = params.get('ref');
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmContent = params.get('utm_content');

  const hasAttributionParams =
    ref || utmSource || utmMedium || utmCampaign || utmContent;

  // Clean URL if attribution params exist (regardless of storage)
  if (hasAttributionParams) {
    ATTRIBUTION_PARAMS.forEach(param => {
      params.delete(param);
    });
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname +
      (newSearch ? `?${newSearch}` : '') +
      window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }

  // Don't overwrite existing attribution (first-touch model)
  if (getAttribution() !== null) {
    return;
  }

  const attribution: Attribution = {};

  if (ref) attribution.ref = ref;
  if (utmSource) attribution.utmSource = utmSource;
  if (utmMedium) attribution.utmMedium = utmMedium;
  if (utmCampaign) attribution.utmCampaign = utmCampaign;
  if (utmContent) attribution.utmContent = utmContent;

  // Only store if we have at least one param
  if (Object.keys(attribution).length > 0) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch {
      // Ignore storage errors (private browsing, storage full, etc.)
    }
  }
}

/**
 * Retrieves stored attribution data.
 */
export function getAttribution(): Attribution | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Attribution) : null;
  } catch {
    return null;
  }
}

/**
 * Clears stored attribution data.
 * Call this after successful sign-up to prevent reuse.
 */
export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

export function setInviteAttribution(): void {
  if (getAttribution() !== null) {
    return;
  }

  const attribution: Attribution = {
    utmSource: 'invite',
    utmMedium: 'referral',
    utmCampaign: 'duo_invite',
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Ignore storage errors
  }
}
